<script setup>
import { ref, computed, onMounted } from 'vue'
import { useSubscriptionStore } from '../../Store/SubscriptionStore.js'
import { useHttpErrorsHandler } from '../../Composables/httpErrorsHandler.js'
import { useToast } from 'vue-toast-notification'
import axios from 'axios'
import Loader from '../../Components/Loader.vue'

const subscriptionStore = useSubscriptionStore()
const { httpErrorsHandler } = useHttpErrorsHandler()
const $toast = useToast()

const loading = ref(true)
const loadingPortal = ref(false)
const plans = ref([])
const showAnnualPlans = ref(false)
const processingPlanId = ref(null)

const currentPlanId = computed(() => subscriptionStore.getCurrentPlan?.id ?? null)

const isCurrentPlan = (planId) => {
    return currentPlanId.value === planId
}

onMounted(async () => {
    try {
        await Promise.all([
            subscriptionStore.fetchPlans(),
            subscriptionStore.fetchSubscription(),
        ])
        plans.value = subscriptionStore.getActivePlans

        // Set default toggle based on current plan or available plans
        const currentPlan = subscriptionStore.getCurrentPlan
        if (currentPlan) {
            showAnnualPlans.value = currentPlan.billing_interval === 'annual'
        } else {
            showAnnualPlans.value = false
        }
    } catch (error) {
        httpErrorsHandler(error)
    } finally {
        loading.value = false
    }
})

const monthlyPlans = computed(() => {
    return plans.value.filter(plan => plan.data.billing_interval === 'monthly')
})

const annualPlans = computed(() => {
    return plans.value.filter(plan => plan.data.billing_interval === 'annual')
})

const hasMultipleBillingIntervals = computed(() => {
    return monthlyPlans.value.length > 0 && annualPlans.value.length > 0
})

const displayedPlans = computed(() => {
    let filteredPlans
    // If only one billing type exists, show all plans
    if (!hasMultipleBillingIntervals.value) {
        filteredPlans = plans.value
    } else {
        // Otherwise filter by selected billing interval
        filteredPlans = showAnnualPlans.value ? annualPlans.value : monthlyPlans.value
    }
    // Sort by price ascending
    return [...filteredPlans].sort((a, b) => a.data.price_cents - b.data.price_cents)
})

const selectPlan = async (planId) => {
    // Les utilisateurs déjà abonnés ne peuvent pas changer de plan via cette interface
    if (subscriptionStore.isSubscribed) {
        return
    }

    // Nouvel utilisateur - redirection vers le checkout
    processingPlanId.value = planId
    try {
        const response = await axios.post('/api/subscription/checkout', {
            plan_id: planId,
        })

        if (response.data.checkout_url) {
            window.location.href = response.data.checkout_url
        }
    } catch (error) {
        $toast.error('Erreur lors de la souscription', {
            position: 'top',
            duration: 5000,
            dismissible: true,
            pauseOnHover: true,
        })
        httpErrorsHandler(error)
        processingPlanId.value = null
    }
}

const goToBillingPortal = async () => {
    loadingPortal.value = true
    try {
        const response = await axios.post('/api/subscription/portal')
        if (response.data.portal_url) {
            window.location.href = response.data.portal_url
        }
    } catch (error) {
        $toast.error('Erreur lors de l\'ouverture du portail de facturation', {
            position: 'top',
            duration: 5000,
            dismissible: true,
            pauseOnHover: true,
        })
    } finally {
        loadingPortal.value = false
    }
}

function formatPriceInteger(value) {
    return Math.floor(value).toString()
}

function getCardStyle(isPopular) {
    if (isPopular) {
        return {
            cardClass: 'card-style-green',
            badgeClass: 'badge-green',
            buttonClass: 'btn-green',
            recommended: true
        }
    }
    return {
        cardClass: 'card-style-black',
        badgeClass: 'badge-black',
        buttonClass: 'btn-black',
        recommended: false
    }
}
</script>

<template>
    <div class="container py-5">
        <!-- Loader -->
        <Loader v-if="loading" />

        <div v-else>
            <!-- Incomplete Payment Alert -->
            <div v-if="subscriptionStore.hasIncompletePayment" class="alert alert-warning d-flex align-items-center mb-4" role="alert">
                <i class="ri-error-warning-line fs-4 me-3"></i>
                <div class="flex-grow-1">
                    <strong>Paiement en attente</strong>
                    <p class="mb-0 mt-1">
                        Votre abonnement a un problème de paiement. Veuillez mettre à jour votre moyen de paiement pour continuer à utiliser nos services.
                    </p>
                </div>
                <button
                    class="btn btn-warning ms-3 text-nowrap"
                    :disabled="loadingPortal"
                    @click="goToBillingPortal"
                >
                    <span v-if="loadingPortal" class="spinner-border spinner-border-sm me-1"></span>
                    Mettre à jour mon paiement
                </button>
            </div>

            <!-- Header -->
            <div class="text-center mb-4">
                <i class="ri-price-tag-3-line fs-36 text-primary mb-3" style="color: #5CB85C !important;"></i>
                <h4 class="fw-semibold fs-24">Nos abonnements</h4>
                <p class="text-muted mb-5 fs-15">
                    Choisissez le plan qui correspond le mieux à vos besoins
                </p>

                <!-- Billing Toggle -->
                <div
                    v-if="hasMultipleBillingIntervals"
                    class="d-flex align-items-center justify-content-center gap-3 mb-3"
                >
                    <span class="fw-semibold" :class="{ 'text-muted': showAnnualPlans }">
                        Mensuel
                    </span>
                    <div class="form-check form-switch form-switch-lg">
                        <input
                            class="form-check-input billing-toggle"
                            type="checkbox"
                            role="switch"
                            id="billingToggle"
                            v-model="showAnnualPlans"
                        >
                    </div>
                    <span class="fw-semibold" :class="{ 'text-muted': !showAnnualPlans }">
                        Annuel
                    </span>
                    <span class="badge badge-discount">-10%</span>
                </div>
            </div>

            <!-- Plans Grid -->
            <div v-if="displayedPlans.length > 0" class="row g-4 justify-content-center">
                <div
                    v-for="plan in displayedPlans"
                    :key="plan.data.id"
                    class="col-auto d-flex plan-col"
                >
                    <div
                        class="card h-100 position-relative"
                        :class="getCardStyle(plan.data.is_popular).cardClass"
                    >
                        <!-- Current Plan Badge -->
                        <div
                            v-if="isCurrentPlan(plan.data.id)"
                            class="position-absolute top-0 start-50 translate-middle"
                        >
                            <span class="badge px-3 py-2 badge-current d-inline-flex align-items-center">
                                <i class="ri-check-line me-1"></i>Plan actuel
                            </span>
                        </div>
                        <!-- Recommended Badge -->
                        <div
                            v-else-if="getCardStyle(plan.data.is_popular).recommended"
                            class="position-absolute top-0 start-50 translate-middle"
                        >
                            <span :class="['badge px-3 py-2', getCardStyle(plan.data.is_popular).badgeClass]">
                                Recommandé
                            </span>
                        </div>

                        <div class="card-body p-4 d-flex flex-column">
                            <!-- Plan Header -->
                            <div class="text-center plan-header">
                                <h5 class="plan-title">{{ plan.data.name }}</h5>

                                <!-- Price Display -->
                                <div class="d-flex align-items-baseline justify-content-center gap-1">
                                    <span class="plan-price">
                                        {{ formatPriceInteger(plan.data.price_cents / 100) }}
                                    </span>
                                    <span class="plan-price-currency">€</span>
                                    <span class="text-muted fs-14">HT</span>
                                    <span class="text-muted fs-14">
                                        / {{ showAnnualPlans ? 'an' : 'mois' }}
                                    </span>
                                </div>
                            </div>

                            <!-- Divider -->
                            <hr class="my-3 opacity-25">

                            <!-- Features List (Description) -->
                            <div
                                v-if="plan.data.description"
                                class="features-list mb-3"
                                v-html="plan.data.description"
                            ></div>

                            <!-- Modules inclus -->
                            <div v-if="plan.data.modules && plan.data.modules.length > 0" class="modules-list flex-grow-1 mb-4">
                                <small class="text-body d-block mb-2 fw-semibold">Modules inclus :</small>
                                <ul class="list-unstyled mb-0">
                                    <li
                                        v-for="module in [...plan.data.modules].sort((a, b) => a.name.localeCompare(b.name, 'fr'))"
                                        :key="module.id"
                                        class="mb-1 d-flex align-items-center"
                                    >
                                        <i class="ri-check-line text-success me-2"></i>
                                        <span class="fs-13">{{ module.name }}</span>
                                    </li>
                                </ul>
                            </div>

                            <!-- CTA Button -->
                            <div class="mt-auto">
                                <!-- Plan actuel -->
                                <button
                                    v-if="isCurrentPlan(plan.data.id)"
                                    type="button"
                                    class="btn w-100 btn-outline-dark d-inline-flex align-items-center justify-content-center"
                                    disabled
                                >
                                    <i class="ri-check-line me-1"></i>Plan actuel
                                </button>
                                <!-- Utilisateur déjà abonné : bouton désactivé avec tooltip -->
                                <span
                                    v-else-if="subscriptionStore.isSubscribed"
                                    v-tooltip="'Pour changer de plan, veuillez contacter votre administrateur via un ticket.'"
                                    class="d-block"
                                >
                                    <button
                                        type="button"
                                        class="btn w-100 btn-secondary d-inline-flex align-items-center justify-content-center"
                                        disabled
                                    >
                                        <i class="ri-lock-line me-1"></i>
                                        Changer de plan
                                    </button>
                                </span>
                                <!-- Paiement en attente : bouton désactivé -->
                                <button
                                    v-else-if="subscriptionStore.hasIncompletePayment"
                                    type="button"
                                    class="btn w-100 btn-secondary d-inline-flex align-items-center justify-content-center"
                                    disabled
                                >
                                    <i class="ri-error-warning-line me-1"></i>
                                    Paiement en attente
                                </button>
                                <!-- Nouvel utilisateur : bouton actif -->
                                <button
                                    v-else
                                    type="button"
                                    :class="['btn w-100 d-inline-flex align-items-center justify-content-center', getCardStyle(plan.data.is_popular).buttonClass]"
                                    :disabled="processingPlanId !== null"
                                    @click="selectPlan(plan.data.id)"
                                >
                                    <span v-if="processingPlanId === plan.data.id" class="spinner-border spinner-border-sm me-2"></span>
                                    <template v-if="processingPlanId === plan.data.id">
                                        Redirection...
                                    </template>
                                    <template v-else>
                                        Sélectionner
                                    </template>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="!loading && displayedPlans.length === 0" class="text-center py-5">
                <i class="ri-inbox-line display-1 text-muted"></i>
                <p class="text-muted mt-3">Aucun plan disponible pour le moment.</p>
            </div>
        </div>

    </div>
</template>

<style scoped>
/* Plan Header */
.plan-header {
    padding-top: 0.5rem;
}

/* Plan Title */
.plan-title {
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #6c757d;
    margin-bottom: 1rem;
}

/* Plan Price */
.plan-price {
    font-size: 3rem;
    font-weight: 700;
    line-height: 1;
    color: #2C3E50;
}

.plan-price-currency {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2C3E50;
}

/* Discount Badge */
.badge-discount {
    background-color: #5CB85C;
    color: white;
    font-weight: 600;
    font-size: 0.75rem;
    padding: 0.35em 0.65em;
    border-radius: 0.375rem;
}

/* Current Plan Badge */
.badge-current {
    background-color: #5CB85C;
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
}

/* Billing Toggle Switch */
.form-switch-lg .form-check-input {
    width: 3.5rem;
    height: 1.75rem;
    cursor: pointer;
}

.billing-toggle {
    background-color: #6c757d;
    border-color: #6c757d;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
}

.billing-toggle:checked {
    background-color: #5CB85C;
    border-color: #5CB85C;
}

.billing-toggle:focus {
    box-shadow: 0 0 0 0.25rem rgba(92, 184, 92, 0.25);
}

/* Features List */
.features-list {
    font-size: 0.875rem;
    line-height: 1.5;
}

.features-list :deep(ul) {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
}

.features-list :deep(li) {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.5rem;
}

.features-list :deep(li)::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: var(--bs-success);
    font-weight: bold;
}

/* Modules List */
.modules-list {
    font-size: 0.875rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 0.75rem;
}

/* Card Styles - Green */
.card-style-green {
    border: 1px solid #5CB85C;
    border-radius: 9px;
    box-shadow: 0 2px 8px rgba(92, 184, 92, 0.1);
}

.badge-green {
    background-color: #5CB85C;
    color: white;
    font-weight: 600;
}

.btn-green {
    background-color: #5CB85C;
    color: white;
    border: none;
    font-weight: 600;
}

.btn-green:hover {
    background-color: #4A9A4A;
    color: white;
}

/* Card Styles - Black */
.card-style-black {
    border: 1px solid #2C3E50;
    border-radius: 9px;
    box-shadow: 0 2px 8px rgba(44, 62, 80, 0.1);
}

.badge-black {
    background-color: #2C3E50;
    color: white;
    font-weight: 600;
}

.btn-black {
    background-color: #2C3E50;
    color: white;
    border: none;
    font-weight: 600;
}

.btn-black:hover {
    background-color: #1A252F;
    color: white;
}

/* Font sizes */
.fs-36 {
    font-size: 2.25rem;
}

.fs-24 {
    font-size: 1.5rem;
}

.fs-15 {
    font-size: 0.9375rem;
}

.fs-14 {
    font-size: 0.875rem;
}

/* Fixed width for plan cards */
.plan-col {
    flex: 0 0 320px;
    width: 320px;
}

.plan-col .card {
    width: 100%;
}

@media (max-width: 767.98px) {
    .plan-col {
        flex: 0 0 100%;
        width: 100%;
    }
}
</style>
